import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { projectImages, projectGalleries } from "@/data/content";

const ProjectsGallery = ({ limit, stagger = true, testIdPrefix = "project-card" }) => {
  const { t } = useLanguage();
  const items = limit ? t.projects.items.slice(0, limit) : t.projects.items;
  const [active, setActive] = useState(null);

  const open = active !== null ? t.projects.items[active] : null;
  const gallery = active !== null ? projectGalleries[active] || [] : [];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
        {items.map((item, i) => (
          <Reveal key={item.title} delay={(i % 2) * 0.08}>
            <button
              onClick={() => setActive(i)}
              data-testid={`${testIdPrefix}-${i}`}
              className={`group block w-full text-left ${
                stagger && i % 2 === 1 ? "md:mt-20" : ""
              }`}
            >
              <div className="overflow-hidden bg-cream-2 aspect-[4/5] relative">
                <img
                  src={projectImages[i]}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-olive/30">
                  <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-cream border border-cream/70 px-5 py-3">
                    View Project
                  </span>
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-5">
                <h3 className="font-serif text-3xl md:text-4xl text-charcoal group-hover:text-sage transition-colors">
                  {item.title}
                </h3>
                <span className="font-mono text-xs text-taupe">{item.year}</span>
              </div>
              <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-taupe mt-1">
                {item.category}
              </p>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-charcoal/70 backdrop-blur-sm p-4 md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setActive(null)}
            data-testid="project-modal"
          >
            <motion.div
              className="relative bg-cream w-full max-w-5xl my-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                data-testid="project-modal-close"
                className="absolute top-4 right-4 z-10 bg-cream/90 hover:bg-olive hover:text-cream text-charcoal w-10 h-10 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>

              <div className="p-8 md:p-14">
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sage">
                  {open.category} · {open.year}
                </span>
                <h2 className="font-serif font-light text-4xl md:text-6xl text-charcoal mt-4 leading-tight">
                  {open.title}
                </h2>
                <p className="text-charcoal/70 font-light leading-relaxed mt-5 max-w-2xl">
                  {open.desc}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
                  {gallery.map((src, gi) => (
                    <div
                      key={gi}
                      className={`overflow-hidden bg-cream-2 ${
                        gi === 0 ? "sm:col-span-2 aspect-[16/10]" : "aspect-[4/3]"
                      }`}
                    >
                      <img
                        src={src}
                        alt={`${open.title} ${gi + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectsGallery;
